import { ROLE_BY_ID } from '../roleTaxonomy';
import type { UserRoleId } from '../roleTaxonomy';

import UserRoleChip from './UserRoleChip';

interface UserRoleChipsProps {
  roles: UserRoleId[];
  /** Maximum chips rendered inline; overflow rolls into a `+N` indicator
   *  with a title-tooltip listing the overflow role labels. Defaults to 2
   *  for the table column; the detail card raises it to fit more. */
  max?: number;
  size?: 'sm' | 'md';
}

/** Multi-role chip stack (BON-105). Renders up to `max` `UserRoleChip`s
 *  inline; the remainder collapse into a `+N` chip whose `title` lists the
 *  hidden role labels for hover-to-reveal. Empty roles fall through to the
 *  existing `UserRoleChip` "no role" placeholder so this component is a
 *  drop-in replacement for sites that previously rendered a single chip. */
export default function UserRoleChips({ roles, max = 2, size = 'md' }: UserRoleChipsProps) {
  if (roles.length === 0) {
    return <UserRoleChip role={undefined} size={size} />;
  }

  const visible = roles.slice(0, max);
  const overflow = roles.slice(max);
  const sm = size === 'sm';
  const overflowChip =
    'inline-flex items-center rounded-full border font-semibold whitespace-nowrap text-stone-600 bg-stone-100 border-stone-200 ' +
    (sm ? 'h-5 px-2 text-[10.5px]' : 'h-[22px] px-2.5 text-[11.5px]');

  return (
    <div className="inline-flex items-center gap-1 flex-wrap">
      {visible.map((r) => (
        <UserRoleChip key={r} role={r} size={size} />
      ))}
      {overflow.length > 0 && (
        <span
          className={overflowChip}
          title={overflow.map((r) => ROLE_BY_ID[r]?.name ?? r).join(', ')}
        >
          +{overflow.length}
        </span>
      )}
    </div>
  );
}
